import { motion } from 'framer-motion'
import { useState } from 'react'
import GlassCard from '../ui/GlassCard'
import GlassButton from '../ui/GlassButton'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'

interface AssetCardProps {
  title: string
  imageUrl: string
  downloadUrl: string
  formats: string[]
  delay?: number
  isTransparent?: boolean
}

const AssetCard = ({ title, imageUrl, downloadUrl, formats, delay = 0, isTransparent }: AssetCardProps) => {
  const { copied, copyToClipboard } = useCopyToClipboard()
  const [imageLoaded, setImageLoaded] = useState(false)

  const handleCopyLink = () => {
    // For the copy link, we want to copy the actual image URL that users can access
    const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${window.location.origin}${downloadUrl}`
    copyToClipboard(fullUrl)
  }

  const handleDownload = async () => {
    // For download, we need to use the actual file URL
    const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${window.location.origin}${downloadUrl}`

    try {
      // Fetch the image as a blob to force download
      const response = await fetch(fullUrl)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = title.replace(/\s+/g, '-').toLowerCase() + '.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // Fallback to simple link click if fetch fails
      const link = document.createElement('a')
      link.href = fullUrl
      link.download = title.replace(/\s+/g, '-').toLowerCase() + '.png'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  return (
    <GlassCard className="p-6" delay={delay}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        <h4 className="text-lg font-medium text-gray-800 mb-4">{title}</h4>

        <div className="relative mb-6">
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
            <motion.img
              src={imageUrl}
              alt={title}
              className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              onLoad={() => setImageLoaded(true)}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse bg-gray-200 w-full h-full"></div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {formats.map((format) => (
            <span
              key={format}
              className="px-3 py-1 bg-blue-100/70 text-blue-700 text-xs font-medium rounded-full"
            >
              {format}
            </span>
          ))}
          {(isTransparent || title.includes('Transparent') || title.includes('Logo + Name')) && (
            <span className="px-3 py-1 bg-green-100/70 text-green-700 text-xs font-medium rounded-full">
              Transparent
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <GlassButton
            onClick={handleDownload}
            className="flex-1"
          >
            Download
          </GlassButton>
          <GlassButton
            onClick={handleCopyLink}
            className={`flex-1 ${copied ? 'bg-green-100/70 text-green-700' : ''}`}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </GlassButton>
        </div>
      </motion.div>
    </GlassCard>
  )
}

export default AssetCard 